/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { CheckedState } from '@radix-ui/react-checkbox';
import type { ShopperBasketsV2 } from '@/scapi';

import { useToast } from '@/components/toast';
import { useBasketUpdater } from '@/providers/basket';
import type { MattressProtectorFields } from '@/lib/mattress-protector-utils';

/**
 * CART-09 — toggle hook for the per-line-item mattress protector add-on.
 *
 * Encapsulates the entire interaction surface so `<ProductItem>` (full
 * cart) and `<MiniCartItem>` (mini cart) share one implementation:
 *
 * - Picks `intent=add` vs `intent=remove` from the current enrichment
 *   state — `c_mattressProtectorSelected` reflects whether a protector
 *   PLI is linked to this mattress server-side.
 * - Submits to `/action/cart-mattress-protector` via a per-item
 *   `useFetcher` so a checkbox click does NOT navigate the route (the
 *   add/remove is a non-navigating mutation per project Performance &
 *   Data rules §10).
 * - Drives optimistic UI from `fetcher.formData`: while the request is
 *   in flight the storefront immediately reflects the intended state,
 *   then snaps back to the server value if the action fails.
 * - Sets `disabled` while in flight to swallow double-clicks; the row
 *   is otherwise interactive whenever the offer is visible.
 * - Calls `useBasketUpdater()` on success so every other basket
 *   consumer (mini-cart, order summary, totals) re-renders with the
 *   newly returned basket without an extra round-trip.
 * - Surfaces a toast on failure (success is silent — the row state
 *   change is its own confirmation).
 *
 * Inputs are deliberately minimal: pass the enriched product item
 * (which already carries the protector fields) and the hook resolves
 * everything else. The `itemId` of the row is used to scope the
 * fetcher so multiple eligible mattresses in the same cart can be
 * toggled independently without their loading states interfering.
 */
export function useMattressProtectorToggle(
    item: (MattressProtectorFields & { itemId?: string; quantity?: number }) | null | undefined
) {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const updateBasket = useBasketUpdater();
    const fetcher = useFetcher<{
        success: boolean;
        basket?: ShopperBasketsV2.schemas['Basket'];
        error?: { code?: string; message?: string };
    }>({ key: `mattress-protector:${item?.itemId ?? 'unknown'}` });

    // Track whether THIS hook instance kicked off the current fetcher
    // submission. Without this, every <MiniCartItem> mounted on the page
    // would observe the same fetcher state (it's not — different keys —
    // but the symmetry trap is real and worth a guard for safety) and
    // either double-toast on errors or swallow them silently after a
    // toast-fatigue reset elsewhere.
    const inFlightIntentRef = useRef<'add' | 'remove' | null>(null);

    const submittedIntent =
        fetcher.state === 'submitting' && fetcher.formData
            ? (fetcher.formData.get('intent') as 'add' | 'remove' | null)
            : null;

    // Optimistic checked state: while a request is in flight, show the
    // intended outcome immediately. Falls back to the server-derived
    // value when idle.
    const serverChecked = Boolean(item?.c_mattressProtectorSelected);
    const checked: CheckedState =
        submittedIntent === 'add' ? true : submittedIntent === 'remove' ? false : serverChecked;

    const disabled = fetcher.state !== 'idle';

    // Sync basket / surface errors when the action completes.
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        const intent = inFlightIntentRef.current;
        if (!intent) return;
        inFlightIntentRef.current = null;

        if (fetcher.data.success && fetcher.data.basket) {
            updateBasket(fetcher.data.basket);
            return;
        }

        addToast(intent === 'add' ? t('miniCart:addOnAddError') : t('miniCart:addOnRemoveError'), 'error');
        // Listing addToast / updateBasket / t explicitly even though they're
        // stable in practice — costs nothing and removes the need for an
        // eslint-disable. If a future provider implementation ever produces
        // a fresh identity per render, this effect will still pick up the
        // latest reference instead of capturing a stale one.
    }, [fetcher.state, fetcher.data, addToast, updateBasket, t]);

    const onCheckedChange = useCallback(
        (next: CheckedState) => {
            if (!item || disabled) return;
            // Indeterminate is never produced by the row component — guard
            // anyway so the hook can't accidentally submit a meaningless
            // form when wired into an unexpected checkbox shape.
            if (next === 'indeterminate') return;

            const wantsChecked = next === true;
            if (wantsChecked === serverChecked) return;

            if (wantsChecked) {
                const protectorSku = item.c_protectorSku;
                if (!protectorSku) {
                    addToast(t('miniCart:addOnAddError'), 'error');
                    return;
                }
                const mattressItemId = item.itemId;
                if (!mattressItemId) {
                    addToast(t('miniCart:addOnAddError'), 'error');
                    return;
                }
                const mattressQuantity = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
                inFlightIntentRef.current = 'add';
                void fetcher.submit(
                    { intent: 'add', protectorSku, mattressItemId, mattressQuantity: String(mattressQuantity) },
                    { method: 'POST', action: '/action/cart-mattress-protector' }
                );
                return;
            }

            const protectorItemId = item.c_mattressProtectorItemId;
            if (!protectorItemId) {
                // No linked protector PLI to remove — server state already
                // matches the requested unchecked state, just no-op.
                return;
            }
            inFlightIntentRef.current = 'remove';
            void fetcher.submit(
                { intent: 'remove', protectorItemId },
                { method: 'POST', action: '/action/cart-mattress-protector' }
            );
        },
        [item, disabled, serverChecked, fetcher, addToast, t]
    );

    return { checked, disabled, onCheckedChange };
}
